import {filter, find, isEmpty, last, map} from 'lodash';
import {useContext, useEffect, useMemo, useState} from 'react';

import {ClientContext} from 'context';
import {useClientSession, usePrevious} from 'hooks';
import {Session} from 'interfaces';
import {TextChatContentType, TextChatMessage} from 'types';
import {sessionUtils} from 'utils';

type Props = {
    session: Session<any>;
    lastSpokenMessageId: string | null;
    onSubmitMessage: (message: Partial<TextChatMessage>) => void;
}

export const ClientIdleReminder: React.FC<Props> = ({session, lastSpokenMessageId, onSubmitMessage}) => {
    const {idleReminder, localParticipant} = useClientSession();
    const {endLocalSession} = useContext(ClientContext);

    const [timeouts, setTimeouts] = useState([]);
    const [stopSession, setStopSession] = useState(false);

    const [selectedIdleMessage, setSelectedIdleMessage] = useState<{
        content: string, finishSession: boolean, timeout: number,
    } | null>(null);

    const prevSelectedIdleMessageContent = usePrevious(selectedIdleMessage?.content);

    const idleReminderMessages = useMemo(() => idleReminder?.enabled ? map(idleReminder?.messages, (message) => ({
        content: message.text,
        finishSession: message.finishSession,
        timeout: message.timeout,
    })) : [], [idleReminder]);

    const messages = useMemo(() => sessionUtils.getTextChatActivityMessages(session?.activities?.[0]?.data?.threads), [session]);

    const lastChatbotMessage = useMemo(() => last(
        filter(messages, (message) => message.from?.id !== localParticipant?.id)), [messages, localParticipant?.id]);

    const lastClientMessage = useMemo(() =>
        last(
            filter(messages, (message) =>
                message.from?.id === localParticipant?.id)
        ),
    [messages, localParticipant?.id]);

    useEffect(() => {

        if (!lastSpokenMessageId || lastSpokenMessageId !== lastChatbotMessage?.id) {
            if (lastClientMessage?.contentType !== TextChatContentType.IdleReminder) {
                setTimeouts((prev) => {
                    if (isEmpty(prev)) {
                        return prev;
                    }
                    for (const id of prev) {
                        clearTimeout(id);
                    }
                    return [];
                });
            }
            return;
        }

        if (lastClientMessage?.contentType === TextChatContentType.IdleReminder) {
            return;
        }

        const _timeouts = [];
        for (const message of idleReminderMessages) {
            const timeoutId = setTimeout(() => {
                setSelectedIdleMessage(message);
                if (message.finishSession) {
                    setStopSession(true);
                }
            }, message.timeout * 1000);
            _timeouts.push(timeoutId);
        }

        setTimeouts(_timeouts);
    }, [
        idleReminderMessages, lastClientMessage?.id, lastClientMessage?.contentType,
        lastChatbotMessage?.id, lastSpokenMessageId,
    ]);

    useEffect(() => {
        if (stopSession) {
            const lastSpokenMessage = find(messages, (message) => message.id === lastSpokenMessageId);
            if (lastSpokenMessage?.content === selectedIdleMessage?.content) {
                endLocalSession();
            }
        }
    }, [lastSpokenMessageId, messages, selectedIdleMessage?.content, stopSession, endLocalSession]);

    useEffect(() => () => {
        for (const timeoutId of timeouts) {
            clearTimeout(timeoutId);
        }
        setSelectedIdleMessage(null);
    }, [timeouts]);

    useEffect(() => {
        if (!selectedIdleMessage?.content || prevSelectedIdleMessageContent === selectedIdleMessage.content) {
            return;
        }

        onSubmitMessage({
            content: selectedIdleMessage?.content,
            contentType: TextChatContentType.IdleReminder,
        });
    }, [onSubmitMessage, prevSelectedIdleMessageContent, selectedIdleMessage?.content]);

    return null;
};
